import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import Login from "./pages/Login";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import RoleProtectedRoute from "./components/auth/RoleProtectedRoute";
import RoleBasedRedirect from "./components/auth/RoleBasedRedirect";
import useLoginStore from "./stores/loginStore";
import { useQueryGetMe } from "./apis/users";
import { IS_PRODUCTION } from "./utils/env";
import "./global.css";
import "@ant-design/v5-patch-for-react-19";
import UserListPage from "./pages/users/UserListPage";
import UserCreatePage from "./pages/users/UserCreatePage";
import UserEditPage from "./pages/users/UserEditPage";
import UserDetailPage from "./pages/users/UserDetailPage";
import GroupListPage from "./pages/groups/GroupListPage";
import GroupCreatePage from "./pages/groups/GroupCreatePage";
import GroupDetailPage from "./pages/groups/GroupDetailPage";
import GroupEditPage from "./pages/groups/GroupEditPage";
import ExamAssignmentListPage from "./pages/exam_assignments/ExamAssignmentListPage";
import ExamAssignmentDetailPage from "./pages/exam_assignments/ExamAssignmentDetailPage";
import ExamAssignmentCreatePage from "./pages/exam_assignments/ExamAssignmentCreatePage";
import ExamAssignmentUpdatePage from "./pages/exam_assignments/ExamAssignmentUpdatePage";
import NoticeListPage from "./pages/notices/NoticeListPage";
import MyExamsPage from "./pages/my_exams/MyExamsPage";
import ExamStartPage from "./pages/my_exams/ExamStartPage";
import ExamPreviewPage from "./pages/my_exams/preview/ExamPreviewPage";
import ExamPaperListPage from "./pages/exam_papers/ExamPaperListPage";
import ExamPaperCreatePage from "./pages/exam_papers/ExamPaperCreatePage";
import ExamPaperDetailPage from "./pages/exam_papers/ExamPaperDetailPage";
import ExamPaperEditPage from "./pages/exam_papers/ExamPaperEditPage";
import ExamSetListPage from "./pages/exam_sets/ExamSetListPage";
import ExamSetDetailPage from "./pages/exam_sets/ExamSetDetailPage";
import ExamSetCreatePage from "./pages/exam_sets/ExamSetCreatePage";
import ExamSetEditPage from "./pages/exam_sets/ExamSetEditPage";
import ExamPaperEditQuestionsPage from "./pages/exam_papers/ExamPaperEditQuestionsPage";
import { ExamProvider } from "./pages/my_exams/context/ExamContext";
import ExamReportPage from "./pages/exam_reports/ExamReportPage";
import ExamResultListPage from "./pages/exam_results/ExamResultListPage";
import ExamReportDetailPage from "./pages/exam_results/ExamReportDetailPage";
import ExamAssignmentAnalysisPage from "./pages/exam_assignments/ExamAssignmentAnalysisPage";
import SentryTestPage from "./pages/dev/SentryTestPage";
import QuillTestPage from "./pages/dev/QuillTestPage";
import ExamConfigListPage from "./pages/exam_configs/ExamConfigListPage";
import ExamConfigCreatePage from "./pages/exam_configs/ExamConfigCreatePage";
import ExamConfigDetailPage from "./pages/exam_configs/ExamConfigDetailPage";
import ExamConfigEditPage from "./pages/exam_configs/ExamConfigEditPage";
import ExamResultViewPage from "./pages/exam_results/ExamResultViewPage";
import TeacherDemoPage from "./pages/teacher/TeacherDemoPage";
import ExamProgressEditAnswersPage from "./pages/exam_progress/ExamProgressEditAnswersPage";
import ExamPaperPrintQuestionsPage from "./pages/exam_papers/ExamPaperPrintQuestionsPage";
import ExamAssignmentPrintQuestionsPage from "./pages/exam_assignments/ExamAssignmentPrintQuestionsPage";
import ExamAssignmentPrintScoreReportsPage from "./pages/exam_assignments/ExamAssignmentPrintScoreReportsPage";
import ScoreConversionTableCreatePage from "./pages/score_conversion_tables/ScoreConversionTableCreatePage";
import ScoreConversionTableDetailPage from "./pages/score_conversion_tables/ScoreConversionTableDetailPage";
import ScoreConversionTableEditPage from "./pages/score_conversion_tables/ScoreConversionTableEditPage";
import { FEATURES } from "./config/features";
import Login2 from "./pages/Login2";

// Sentry와 Router 연동
const SentryRoutes = Sentry.withSentryRouting(Routes);

function App() {
  const { isAuthenticated } = useLoginStore();
  
  // 토큰이 있을 때만 사용자 정보 가져오기
  const { data: user } = useQueryGetMe({
    enabled: isAuthenticated && IS_PRODUCTION,
  });

  // 사용자 정보가 로드되면 Sentry에 설정
  useEffect(() => {
    if (user && IS_PRODUCTION) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name || user.email,
        role: user.role,
      });
      console.log("Sentry user set:", user.email);
    }
  }, [user]);

  return (
    <Router>
      <SentryRoutes>
        {/* 공개 라우트 */}
        <Route path="/login" element={<Login />} />
        <Route path="/login2" element={<Login2 />} />
        <Route path="/dev/sentry" element={<SentryTestPage />} />
        <Route path="/dev/quill" element={<QuillTestPage />} />

        {/* 보호된 라우트 */}
        <Route element={<ProtectedRoute />}>
          {/* 홈 페이지 - 역할에 따라 리다이렉트됨 */}
          <Route path="/" element={<RoleBasedRedirect />} />

          <Route path="/my_exams" element={<MyExamsPage />} />
          <Route
            path="/my_exams/:id/exam_paper/:exam_paper_id/take"
            element={
              <ExamProvider>
                <ExamStartPage />
              </ExamProvider>
            }
          />
          <Route
            path="/exam_progress/:id/report"
            element={<ExamReportPage />}
          />

          {/* Student-only routes */}
          <Route element={<RoleProtectedRoute requiredRole="student" />}>
            <Route
              path="/exam_results/view/:id/:paperId"
              element={<ExamResultViewPage />}
            />
          </Route>

          {/* 관리자용 라우트 */}
          <Route element={<RoleProtectedRoute requiredRole="admin" />}>
            {/* 개발용 페이지 */}

            {/* 사용자 관리 */}
            <Route path="/users" element={<UserListPage />} />
            <Route path="/users/create" element={<UserCreatePage />} />
            <Route path="/users/:id" element={<UserDetailPage />} />
            <Route path="/users/:id/edit" element={<UserEditPage />} />

            {/* 그룹 관리 */}
            <Route path="/groups" element={<GroupListPage />} />
            <Route path="/groups/create" element={<GroupCreatePage />} />
            <Route path="/groups/:id" element={<GroupDetailPage />} />
            <Route path="/groups/:id/edit" element={<GroupEditPage />} />

            {/* 시험 할당 관리 */}
            <Route
              path="/exam_assignments"
              element={<ExamAssignmentListPage />}
            />
            <Route
              path="/exam_assignments/create"
              element={<ExamAssignmentCreatePage />}
            />
            <Route
              path="/exam_assignments/:id"
              element={<ExamAssignmentDetailPage />}
            />
            <Route
              path="/exam_assignments/:id/edit"
              element={<ExamAssignmentUpdatePage />}
            />
            <Route
              path="/exam_assignments/:id/results/analysis"
              element={<ExamAssignmentAnalysisPage />}
            />
            <Route
              path="/exam_assignments/:id/print-questions"
              element={<ExamAssignmentPrintQuestionsPage />}
            />
            <Route
              path="/exam_assignments/:id/print-score-reports"
              element={<ExamAssignmentPrintScoreReportsPage />}
            />

            {/* 시험지 관리 */}
            <Route path="/exam_papers" element={<ExamPaperListPage />} />
            <Route
              path="/exam_papers/create"
              element={<ExamPaperCreatePage />}
            />
            <Route path="/exam_papers/:id" element={<ExamPaperDetailPage />} />
            <Route
              path="/exam_papers/:id/edit"
              element={<ExamPaperEditPage />}
            />
            <Route
              path="/exam_papers/:id/edit_questions"
              element={<ExamPaperEditQuestionsPage />}
            />
            <Route
              path="/exam_papers/:id/preview/:module"
              element={<ExamPreviewPage />}
            />
            <Route path="/exam_papers/:id/demo" element={<TeacherDemoPage />} />
            <Route
              path="/exam_papers/:id/print/questions"
              element={<ExamPaperPrintQuestionsPage />}
            />

            {/* 시험 세트 관리 */}
            <Route path="/exam_sets" element={<ExamSetListPage />} />
            <Route path="/exam_sets/create" element={<ExamSetCreatePage />} />
            <Route path="/exam_sets/:id" element={<ExamSetDetailPage />} />
            <Route path="/exam_sets/:id/edit" element={<ExamSetEditPage />} />

            {/* 시험 결과 관리 */}
            {FEATURES.ShowExamResultPage && (
              <>
                <Route path="/exam_results" element={<ExamResultListPage />} />
                <Route
                  path="/exam_results/:id"
                  element={<ExamReportDetailPage />}
                />
              </>
            )}

            {/* 시험 설정 관리 */}
            <Route path="/exam_configs" element={<ExamConfigListPage />} />
            <Route
              path="/exam_configs/create"
              element={<ExamConfigCreatePage />}
            />
            <Route
              path="/exam_configs/:id"
              element={<ExamConfigDetailPage />}
            />
            <Route
              path="/exam_configs/:id/edit"
              element={<ExamConfigEditPage />}
            />

            {/* 시험 진행 관리 */}
            <Route
              path="/exam_progress/:id/edit_answers"
              element={<ExamProgressEditAnswersPage />}
            />

            {/* 점수 변환 테이블 관리 */}
            <Route path="/score_conversion_tables/create" element={<ScoreConversionTableCreatePage />} />
            <Route path="/score_conversion_tables/:id" element={<ScoreConversionTableDetailPage />} />
            <Route path="/score_conversion_tables/:id/edit" element={<ScoreConversionTableEditPage />} />

            {/* 공지사항 관리 */}
            <Route path="/notices" element={<NoticeListPage />} />
          </Route>
        </Route>

        {/* 404 페이지 - 모든 경로와 일치하지 않을 때 표시 */}
        <Route path="*" element={<NotFound />} />
      </SentryRoutes>
    </Router>
  );
}

export default App;
